"use client";

import styles from './_footer.module.scss';
import Button from "@/components/button";
import CustomImage from "@/components/image";
import { LayoutContext } from '@/components/layout-context-container';
import { useContext, useMemo } from "react";
import { renderToString } from 'react-dom/server';
import { useRouter } from 'next/router';

/**
 * @description Footer component for DTC/HCP layout
 * 
 * @component
 * 
 * @param {object} props 
 * @param {string|import('react').JSX.Element} [props.code] - Footer code (e.g., 'CP-CFT-US-0263v4')
 * @param {string|import('react').JSX.Element} [props.codeDate] - Footer code date (e.g., '11/2025')
 * @param {string|import('react').JSX.Element} [props.codeYear] - Footer code year (e.g., '2025')
 * @param {Array<number>} [props.disableFooterLinks] - Array of link indices to disable
 * @param {boolean} [props.isDtc] - Flag indicating if the footer is for DTC
 * @returns 
 */
export default function Footer({
  code = 'CP-CFT-US-0263v4',
  codeDate = '11/2025',
  codeYear = '2025',
  disableFooterLinks = [],
  isDtc = false,
}) {
  const router = useRouter();
  const {footerOverrideEndMatter} = useContext(LayoutContext);

  const links = useMemo(() => ([
    {label: <>PRIVACY POLICY</>, href: 'https://www.neurocrine.com/privacy-policy/', targetBlank: true},
    {label: <>COOKIE POLICY</>, href: 'https://www.neurocrine.com/cookie-policy/', targetBlank: true},
    {label: <>COOKIES SETTING</>, cookie: true},
    {label: <>ACCESSIBILITY</>, href: 'https://www.neurocrine.com/neurocrine-accessibility-statement/', targetBlank: true},
    {label: <>TERMS & CONDITIONS</>, href: 'https://www.neurocrine.com/terms-conditions/', targetBlank: true},
    !isDtc && {label: <>CONTACT MEDICAL INFORMATION</>, href: 'https://www.neurocrinemedical.com/', targetBlank: true},
    {label: <span>CRENESSITY<sup>&reg;</sup> SUPPORT PROGRAM</span>, href: isDtc ? 'https://crenessity.neurocrineaccesssupport.com/patient/?utm_source=crenessitysite&utm_medium=referral&ref_page=crn-patient-footer' : 'https://crenessity.neurocrineaccesssupport.com/hcp/?utm_source=crenessityhcpsite&utm_medium=referral&ref_page=crn-hcp-footer', targetBlank: true},
  ]).filter(Boolean), [isDtc]);

  return (
    <>
      <footer className={`footer ${styles.container}`}>
        <div className="container">
          <div className={styles.wrapper}>
            <div className={styles.logo}>
              <Button
                href="https://www.neurocrine.com/"
                targetBlank
                data-gtm-event-category="Footer"
                data-gtm-event-action="Click"
                data-gtm-event-label="Neurocrine Biosciences"
              >
                <CustomImage
                  src="/images/logos/hcp/Neurocrine-Biosciences-Logo.svg"
                  width={187}
                  height={34}
                  alt="Neurocrine Biosciences logo, click to visit corporate website"
                />
              </Button>
            </div>
            <div className={styles.body}>
              <p className={styles.links}>
                {links.map(({label, href, targetBlank, leaveSiteLink, cookie}, i) => {

                  if (cookie) {
                    return (
                      // <!-- OneTrust cookies settings button start -->
                      // Not relying on OneTrust's own one-time '.ot-sdk-show-settings' click
                      // binding: it doesn't survive this button unmounting/remounting (e.g.
                      // navigating to/from a page that bypasses the default layout), so we
                      // drive it directly via OneTrust's documented API instead.
                        <button
                          key={i}
                          id="ot-sdk-btn"
                          type="button"
                          suppressHydrationWarning
                          className={styles.cookieSettings}
                          onClick={() => window.OneTrust?.ToggleInfoDisplay()}
                        >Your Privacy Choices</button>
                      // <!-- OneTrust cookies settings button end -->
                    )
                  }

                  return (
                    !disableFooterLinks?.includes(i) &&
                      <Button
                        key={i}
                        href={href}
                        targetBlank={targetBlank}
                        leaveSiteLink={leaveSiteLink}
                        data-gtm-event-category="Footer"
                        data-gtm-event-action="Click"
                        data-gtm-event-label={renderToString(label)}
                      >{label}</Button>
                  )
                })}
              </p>
              <p className={styles.caption}>
                ©{footerOverrideEndMatter?.codeYear || codeYear} Neurocrine Biosciences, Inc. All Rights Reserved. {footerOverrideEndMatter?.code !== '' && code !== null && code !== '' && <><span className="text-nowrap">{ footerOverrideEndMatter ? footerOverrideEndMatter?.code : code}</span>&nbsp;</>} {footerOverrideEndMatter?.codeDate || codeDate}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
